	import React, { useState, useEffect } from 'react';
	import { CheckCircle, Users, Clock, Calendar, BarChart2, Target, Award } from 'lucide-react';

	const CircularProjectLoader = ({ isLoading, onLoadComplete }) => {
	  const [progress, setProgress] = useState(0);
	  const [activePhase, setActivePhase] = useState(0);
	  
	  const projectPhases = [
		{ icon: <Target size={20} />, label: "Initializing", color: "text-indigo-500" },
		{ icon: <Users size={20} />, label: "Team Assembly", color: "text-blue-500" },
		{ icon: <Calendar size={20} />, label: "Planning", color: "text-cyan-500" },
		{ icon: <Clock size={20} />, label: "Executing", color: "text-teal-500" },
		{ icon: <BarChart2 size={20} />, label: "Monitoring", color: "text-green-500" },
		{ icon: <Award size={20} />, label: "Completion", color: "text-emerald-500" }
	  ];

	  useEffect(() => {
		if (!isLoading) return;
		
		let timer;
		if (progress < 100) {
		  timer = setTimeout(() => {
			// Controlled increment with acceleration
			const remaining = 100 - progress;
			let increment;
			
			if (remaining <= 5) {
			  increment = remaining; // Exact remaining when close to completion
			} else {
			  // Accelerating increment based on progress
			  const baseSpeed = 5;
			  const acceleration = progress * 0.1;
			  increment = Math.min(baseSpeed + acceleration, remaining);
			}

			const nextProgress = Math.min(progress + increment, 100);
			setProgress(nextProgress);

			const newPhase = Math.min(
			  Math.floor(nextProgress / (100 / projectPhases.length)),
			  projectPhases.length - 1
			);
			setActivePhase(newPhase);

			if (nextProgress >= 100) {
			  setTimeout(() => {
				onLoadComplete?.();
				// Reset progress and phase after completion
				setProgress(0);
				setActivePhase(0);
			  }, 600);
			}
		  }, 200);
		} else {
		  // If progress is already at 100, reset immediately
		  setProgress(0);
		  setActivePhase(0);
		}
		
		return () => clearTimeout(timer);
	  }, [progress, isLoading, onLoadComplete, projectPhases.length]);

	  if (!isLoading) return null;

	  const radius = 100;
	  const circumference = 2 * Math.PI * radius;
	  const strokeDashoffset = circumference - (progress / 100) * circumference;
	  
	  return (
		<div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-95 z-50">
		  <div className="relative flex flex-col items-center">
			<div className="relative w-64 h-64">
			  <svg className="w-full h-full" viewBox="0 0 220 220">
				<circle 
				  cx="110" 
				  cy="110" 
				  r={radius} 
				  fill="none" 
				  stroke="#e5e7eb" 
				  strokeWidth="8"
				  className="opacity-30"
				/>
				
				<circle 
				  cx="110" 
				  cy="110" 
				  r={radius} 
				  fill="none" 
				  stroke="url(#gradient)" 
				  strokeWidth="10"
				  strokeLinecap="round"
				  strokeDasharray={circumference}
				  strokeDashoffset={strokeDashoffset}
				  transform="rotate(-90 110 110)"
				  className="transition-all duration-300 ease-out"
				/>
				
				<defs>
				  <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
					<stop offset="0%" stopColor="#6366f1" />
					<stop offset="50%" stopColor="#8b5cf6" />
					<stop offset="100%" stopColor="#ec4899" />
				  </linearGradient>
				</defs>
			  </svg>
			  
			  <div className="absolute inset-0 flex flex-col items-center justify-center">
				<div className="text-5xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 bg-clip-text text-transparent">
				  {Math.round(progress)}%
				</div>
				           <div className="text-gray-600 font-medium mt-2">
              {projectPhases[activePhase].label}
            </div>
			  </div>
			</div>
				 <div className="mt-8 grid grid-cols-6 gap-2 max-w-md">
          {projectPhases.map((phase, index) => (
            <div 
              key={index} 
              className={`flex flex-col items-center transition-all duration-300 ${
                index === activePhase ? 'scale-110' : (index < activePhase ? 'opacity-70' : 'opacity-40')
              }`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300 ${
                index < activePhase 
                  ? 'bg-gray-100 text-gray-500' 
                  : index === activePhase
                    ? `bg-white shadow-lg ${phase.color}`
                    : 'bg-gray-100 text-gray-400'
              }`}>
                {index < activePhase ? (
                  <CheckCircle size={16} />
                ) : index === activePhase ? (
                  <div className={`${phase.color} animate-pulse`}>{phase.icon}</div>
                ) : (
                  phase.icon
                )}
              </div>
              <div className={`text-xs mt-2 font-medium whitespace-nowrap ${
                index === activePhase ? 'text-gray-800' : 'text-gray-500'
              }`}>
                {phase.label}
              </div>
            </div>
          ))}
        </div>
		  </div>
		</div>
	  );
	};

	export default CircularProjectLoader;